import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { Apollo, gql } from 'apollo-angular';
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, Observable, firstValueFrom, from, map, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface RefreshSessionInfo {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  device: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  current: boolean;
}

export interface RespGeneral {
  ok: boolean;
  message: string;
}

export interface PushSubscriptionInfo {
  id: string;
  endpoint: string;
  device: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly persistedSessionMarkerKey = 'session-active';
  private readonly pushPromptDismissedKeyPrefix = 'push-prompt-dismissed';
  private readonly pushPromptSnoozeMs = 1000 * 60 * 60 * 24 * 7;
  private readonly sessionStorageKeys = ['roll', 'name', 'email', 'access'];
  public readonly apiBaseUrl = environment.uri.replace(/\/graphql$/, '');
  private readonly sessionCheckIntervalMs = 15000;
  private refreshPromise: Promise<string | null> | null = null;
  private accessToken: string | null = null;
  private apolloInstance: Apollo | null = null;
  private sessionMonitorId: number | null = null;
  private checkingSession = false;

  public $loginSubjet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  private access: any | null = null;
  private roll: string | null = null;
  private USA: boolean;

  private normalizeOptionalField(value: unknown): string | null {
    if (value == null) {
      return null;
    }

    const normalizedValue = String(value).trim();
    return normalizedValue ? normalizedValue : null;
  }

  constructor(private injector: Injector, private router: Router, private http: HttpClient, private swPush: SwPush) {
    this.roll && this.roll === 'ADMINISTRADOR'
      ? (this.USA = true)
      : (this.USA = false);
  }

  private get apollo(): Apollo {
    if (!this.apolloInstance) {
      this.apolloInstance = this.injector.get(Apollo);
    }

    return this.apolloInstance;
  }

  

  isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }



  login(inputData: any): Observable<any> {
    const queryLogin = gql`
      query Login($email: String!, $pass: String!) {
        login(email: $email, pass: $pass) {
          token
          roll
          name
          email
          access {
            table {
              name
              id
            }
            read
            write
            delete
          }
        }
      }
    `;
    return this.apollo
      .watchQuery<any>({
        fetchPolicy: 'no-cache',
        query: queryLogin,
        variables: {
          email: inputData.email,
          pass: inputData.pass,
        },
      })
      .valueChanges.pipe<any>(
        map((resp) => {
          this.saveLocalStorage(resp.data);
          return resp.data;
        })
      );
  }

  logout(): void {
    this.stopSessionMonitor();
    this.revokeRefreshSession().subscribe({
      error: () => {
        return;
      },
    });

    this.clearSessionStorage();
    this.router.navigate(['/login']);
  }

  logoutAllSessions(): void {
    this.stopSessionMonitor();
    this.revokeAllRefreshSessions().subscribe({
      next: () => {
        this.clearSessionStorage();
        this.router.navigate(['/login']);
      },
      error: () => {
        this.clearSessionStorage();
        this.router.navigate(['/login']);
      },
    });
  }

  activateUser(user: string): Observable<any> {
    const mutation = gql`
      mutation ActivateUser($user: String!) {
        activateUser(user: $user) {
          ok
          message
        }
      }
    `;
    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          user,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.activateUser;
        })
      );
  }

  getToken(): string | null {
    return this.accessToken;
  }

  isAdministrator(): boolean {
    return this.getRoll() === 'ADMINISTRADOR';
  }

  canUsePushNotifications(): boolean {
    return environment.production && this.swPush.isEnabled && typeof Notification !== 'undefined';
  }

  async shouldShowPushNotificationsPrompt(): Promise<boolean> {
    if (!this.canUsePushNotifications() || !this.hasStoredSession()) {
      return false;
    }

    if (Notification.permission !== 'default') {
      return false;
    }

    const dismissedKey = this.getPushPromptDismissedStorageKey();
    const dismissedAt = dismissedKey ? this.getPushPromptDismissedAt(dismissedKey) : null;
    if (dismissedAt !== null && Date.now() - dismissedAt < this.pushPromptSnoozeMs) {
      return false;
    }

    try {
      const existingSubscription = await firstValueFrom(this.swPush.subscription);
      return !existingSubscription;
    } catch {
      return true;
    }
  }

  dismissPushNotificationsPrompt(): void {
    const dismissedKey = this.getPushPromptDismissedStorageKey();
    if (!dismissedKey) {
      return;
    }

    localStorage.setItem(dismissedKey, String(Date.now()));
  }

  clearDismissedPushNotificationsPrompt(): void {
    const dismissedKey = this.getPushPromptDismissedStorageKey();
    if (!dismissedKey) {
      return;
    }

    localStorage.removeItem(dismissedKey);
  }

  private getPushPromptDismissedStorageKey(): string | null {
    const email = String(localStorage.getItem('email') ?? '').trim().toLowerCase();
    if (!email) {
      return null;
    }

    return `${this.pushPromptDismissedKeyPrefix}:${email}`;
  }

  private getPushPromptDismissedAt(storageKey: string): number | null {
    const rawValue = String(localStorage.getItem(storageKey) ?? '').trim();
    if (!rawValue) {
      return null;
    }

    if (rawValue === 'true') {
      const migratedValue = Date.now();
      localStorage.setItem(storageKey, String(migratedValue));
      return migratedValue;
    }

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  getPushNotificationsAvailabilityMessage(): string | null {
    if (!environment.production) {
      return 'Las notificaciones push solo estan disponibles en la version publicada por HTTPS.';
    }

    if (this.swPush.isEnabled && typeof Notification !== 'undefined') {
      return null;
    }

    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent ?? '';
      const isIosDevice = /iPad|iPhone|iPod/i.test(userAgent);
      const isStandaloneMode = typeof window !== 'undefined'
        && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

      if (isIosDevice && !isStandaloneMode) {
        return 'En iPhone o iPad debes instalar la app en la pantalla de inicio y abrirla como PWA para activar notificaciones.';
      }
    }

    if (!this.swPush.isEnabled) {
      return 'Este navegador no permite notificaciones push para esta app en el modo actual.';
    }

    return 'Este navegador no expone la API de notificaciones.';
  }

  saveLocalStorage(data: any) {
    const sessionData = data?.login ?? data?.refreshSession ?? data;
    if (!sessionData) {
      return;
    }

    this.accessToken = sessionData.token ?? null;
    localStorage.setItem('roll', sessionData.roll);
    localStorage.setItem('name', sessionData.name);
    localStorage.setItem('email', sessionData.email);
    localStorage.setItem('access', JSON.stringify(sessionData.access));
    localStorage.setItem(this.persistedSessionMarkerKey, 'true');
    this.startSessionMonitor();
    void this.syncExistingPushSubscription();

    //const access = JSON.parse(localStorage.getItem("access")!);
  }

  hasStoredSession(): boolean {
    return Boolean(this.getToken() || localStorage.getItem(this.persistedSessionMarkerKey) === 'true');
  }

  async ensureValidSession(): Promise<string | null> {
    const accessToken = this.getToken();
    if (accessToken && !this.isTokenExpired(accessToken)) {
      return accessToken;
    }

    if (!this.hasStoredSession()) {
      return null;
    }

    if (!this.refreshPromise) {
      this.refreshPromise = firstValueFrom(this.refreshSession())
        .then((resp: any) => {
          this.saveLocalStorage(resp);
          return resp?.token ?? null;
        })
        .catch(() => {
          this.clearSessionStorage();
          return null;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private clearSessionStorage(): void {
    this.stopSessionMonitor();
    this.accessToken = null;
    this.sessionStorageKeys.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(this.persistedSessionMarkerKey);
  }

  private startSessionMonitor(): void {
    if (typeof window === 'undefined' || this.sessionMonitorId !== null) {
      return;
    }

    this.sessionMonitorId = window.setInterval(() => {
      void this.validateSessionInBackground();
    }, this.sessionCheckIntervalMs);
  }

  private stopSessionMonitor(): void {
    if (typeof window === 'undefined' || this.sessionMonitorId === null) {
      return;
    }

    window.clearInterval(this.sessionMonitorId);
    this.sessionMonitorId = null;
    this.checkingSession = false;
  }

  private async validateSessionInBackground(): Promise<void> {
    if (this.checkingSession || !this.hasStoredSession()) {
      return;
    }

    this.checkingSession = true;

    try {
      const isValid = await firstValueFrom(this.checkToken());
      if (!isValid) {
        this.forceLogoutToLogin();
      }
    } catch {
      this.forceLogoutToLogin();
    } finally {
      this.checkingSession = false;
    }
  }

  private forceLogoutToLogin(): void {
    this.clearSessionStorage();
    this.router.navigate(['/login']);
  }

  private async syncExistingPushSubscription(): Promise<void> {
    if (!this.canUsePushNotifications() || !this.hasStoredSession()) {
      return;
    }

    try {
      const existingSubscription = await firstValueFrom(this.swPush.subscription);
      if (!existingSubscription) {
        return;
      }

      await firstValueFrom(this.savePushSubscription(existingSubscription));
    } catch {
      return;
    }
  }

  async enablePushNotifications(): Promise<RespGeneral> {
    if (!this.canUsePushNotifications()) {
      throw new Error('Las notificaciones push solo estan disponibles en la PWA publicada por HTTPS.');
    }

    const existingSubscription = await firstValueFrom(this.swPush.subscription);
    const subscription = existingSubscription ?? await this.swPush.requestSubscription({
      serverPublicKey: await firstValueFrom(this.getPushNotificationPublicKey()),
    });

    this.clearDismissedPushNotificationsPrompt();

    return firstValueFrom(this.savePushSubscription(subscription));
  }

  getMyPushSubscriptions(): Observable<PushSubscriptionInfo[]> {
    return this.executeAuthenticatedSessionRequest<PushSubscriptionInfo[]>(
      `query MyPushSubscriptions {
        myPushSubscriptions {
          id
          endpoint
          device
          userAgent
          ipAddress
          createdAt
          updatedAt
        }
      }`,
      'myPushSubscriptions'
    );
  }

  removePushSubscriptionById(id: string): Observable<RespGeneral> {
    return from(this.removeLocalPushSubscriptionIfCurrent(id)).pipe(
      switchMap(() => this.executeAuthenticatedSessionRequest<RespGeneral>(
        `mutation RemovePushSubscriptionById($id: String!) {
          removePushSubscriptionById(id: $id) {
            ok
            message
          }
        }`,
        'removePushSubscriptionById',
        { id }
      ))
    );
  }

  private getPushNotificationPublicKey(): Observable<string> {
    return this.executeAuthenticatedSessionRequest<string>(
      `query PushNotificationPublicKey {
        pushNotificationPublicKey
      }`,
      'pushNotificationPublicKey'
    );
  }

  private savePushSubscription(subscription: PushSubscription): Observable<RespGeneral> {
    return this.executeAuthenticatedSessionRequest<RespGeneral>(
      `mutation SavePushSubscription($subscription: String!) {
        savePushSubscription(subscription: $subscription) {
          ok
          message
        }
      }`,
      'savePushSubscription',
      {
        subscription: JSON.stringify(subscription.toJSON()),
      }
    );
  }

  private async removeLocalPushSubscriptionIfCurrent(subscriptionId: string): Promise<void> {
    try {
      const [subscriptions, currentSubscription] = await Promise.all([
        firstValueFrom(this.getMyPushSubscriptions()),
        firstValueFrom(this.swPush.subscription),
      ]);

      const selectedSubscription = (subscriptions ?? []).find((item) => item.id === subscriptionId);
      if (!selectedSubscription || !currentSubscription) {
        return;
      }

      if (!selectedSubscription.endpoint.includes(currentSubscription.endpoint.slice(-16))) {
        return;
      }

      await currentSubscription.unsubscribe();
    } catch {
      return;
    }
  }

  refreshSession(): Observable<any> {
    return this.http
      .post<any>(environment.uri, {
        query: `mutation RefreshSession {
          refreshSession {
            token
            roll
            name
            email
            access {
              read
              write
              delete
              table {
                id
                name
              }
            }
          }
        }`,
      }, {
        withCredentials: true,
      })
      .pipe(
        map((resp: any) => {
          if (resp?.errors?.length) {
            throw new Error(resp.errors[0]?.message || 'No se pudo renovar la sesion');
          }

          return resp.data.refreshSession;
        })
      );
  }

  private executeAuthenticatedSessionRequest<T>(query: string, fieldName: string, variables?: Record<string, unknown>): Observable<T> {
    return from(this.ensureValidSession()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No autorizado'));
        }

        return this.http.post<any>(environment.uri, {
          query,
          variables,
        }, {
          withCredentials: true,
          headers: {
            authorization: token,
          },
        });
      }),
      map((resp: any) => {
        if (resp?.errors?.length) {
          throw new Error(resp.errors[0]?.message || 'Error de sesion');
        }

        return resp?.data?.[fieldName] as T;
      })
    );
  }

  revokeRefreshSession(): Observable<boolean> {
    return this.http
      .post<any>(environment.uri, {
        query: `mutation RevokeRefreshSession {
          revokeRefreshSession
        }`,
      }, {
        withCredentials: true,
      })
      .pipe(
        map((resp: any) => {
          return Boolean(resp?.data?.revokeRefreshSession);
        })
      );
  }

  revokeAllRefreshSessions(): Observable<boolean> {
    return this.executeAuthenticatedSessionRequest<boolean>(
      `mutation RevokeAllRefreshSessions {
        revokeAllRefreshSessions
      }`,
      'revokeAllRefreshSessions'
    );
  }

  getMyRefreshSessions(): Observable<RefreshSessionInfo[]> {
    return this.executeAuthenticatedSessionRequest<RefreshSessionInfo[]>(
      `query MyRefreshSessions {
        myRefreshSessions {
          id
          userId
          userName
          userEmail
          device
          userAgent
          ipAddress
          createdAt
          lastUsedAt
          expiresAt
          current
        }
      }`,
      'myRefreshSessions'
    );
  }

  revokeRefreshSessionById(id: string): Observable<boolean> {
    return this.executeAuthenticatedSessionRequest<boolean>(
      `mutation RevokeRefreshSessionById($id: String!) {
        revokeRefreshSessionById(id: $id)
      }`,
      'revokeRefreshSessionById',
      { id }
    );
  }

  passForgotten(inputData: any): Observable<any> {
    const mutattionChangePass = gql`
      mutation ForgottenPass($email: String!) {
        forgottenPass(email: $email)
      }
    `;

    return this.apollo
      .mutate<any>({
        mutation: mutattionChangePass,
        variables: {
          email: inputData.email,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  updatePass(inputData: any): Observable<any> {
    const mutattionChangePass = gql`
      mutation ChangePass($pass: String!, $tokenPass: String!) {
        changePass(pass: $pass, tokenPass: $tokenPass) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate<any>({
        fetchPolicy: 'no-cache',
        mutation: mutattionChangePass,
        variables: {
          pass: inputData.pass,
          tokenPass: inputData.tokenPass,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.changePass;
        })
      );
  }

  checkToken(): Observable<any> {
    return this.executeAuthenticatedSessionRequest<boolean>(
      `query CheckToken {
        checkToken
      }`,
      'checkToken'
    );
  }

  newUser(data: FormGroup, permisos: FormGroup): Observable<any> {
    const myArray = this.buildAccessPayload(permisos);

    const mutation = gql`
      mutation CreateUser(
        $name: String!
        $email: String!
        $pass: String!
        $roll: User_roll!
        $access: [inputAccess]!
      ) {
        createUser(
          name: $name
          email: $email
          pass: $pass
          roll: $roll
          access: $access
        ) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          name: data.controls['name'].value,
          email: data.controls['email'].value,
          pass: data.controls['pass'].value,
          roll: data.controls['roll'].value.name,
          access: myArray,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.createUser;
        })
      );
  }

  createTechnician(data: FormGroup): Observable<any> {
    const mutation = gql`
      mutation CreateTechnicianUser($data: inputTechnicianUser!) {
        createTechnicianUser(data: $data) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          data: {
            nombre: data.get('nombre')?.value,
            apellidos: data.get('apellidos')?.value,
            correo: data.get('correo')?.value,
            telefono: data.get('telefono')?.value,
            dni: this.normalizeOptionalField(data.get('dni')?.value),
            estado: data.get('estado')?.value?.value ?? data.get('estado')?.value,
            foto: data.get('foto')?.value,
          },
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.createTechnicianUser;
        })
      );
  }

  loadTechnicians(skip = 0, take = 40): Observable<any> {
    const query = gql`
      query LoadTechnicians($take: Int!, $skip: Int!) {
        loadTechnicians(take: $take, skip: $skip) {
          users {
            id
            name
            email
            telefono
            dni
            estado
            active
            disable
            foto
            createdAt
          }
          totalRecords
        }
      }
    `;

    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: { skip, take },
      })
      .pipe(map((resp: any) => resp.data.loadTechnicians));
  }

  searchTechnicians(search: string, skip = 0, take = 40): Observable<any> {
    const query = gql`
      query SearchTechnicians($search: String!, $take: Int!, $skip: Int!) {
        searchTechnicians(search: $search, take: $take, skip: $skip) {
          users {
            id
            name
            email
            telefono
            dni
            estado
            active
            disable
            foto
            createdAt
          }
          totalRecords
        }
      }
    `;

    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: { search, skip, take },
      })
      .pipe(map((resp: any) => resp.data.searchTechnicians));
  }

  loadTechnicianById(id: string): Observable<any> {
    const query = gql`
      query LoadTechnicianById($id: String!) {
        loadTechnicianById(id: $id) {
          id
          name
          createdAt
          telefono
          dni
          foto
          email
          estado
          active
          disable
        }
      }
    `;

    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: { id },
      })
      .pipe(map((resp: any) => resp.data.loadTechnicianById));
  }

  updateTechnician(id: string, data: FormGroup): Observable<any> {
    const mutation = gql`
      mutation UpdateTechnicianUser($id: String!, $data: inputTechnicianUserUpdate!) {
        updateTechnicianUser(id: $id, data: $data) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          id,
          data: {
            nombre: data.get('nombre')?.value,
            apellidos: data.get('apellidos')?.value,
            correo: data.get('correo')?.value,
            telefono: data.get('telefono')?.value,
            dni: this.normalizeOptionalField(data.get('dni')?.value),
            estado: data.get('estado')?.value?.value ?? data.get('estado')?.value,
            foto: data.get('foto')?.value,
          },
        },
      })
      .pipe(map((resp: any) => resp.data.updateTechnicianUser));
  }

  deleteTechnician(id: string): Observable<any> {
    const mutation = gql`
      mutation DeleteTechnicianUser($id: String!) {
        deleteTechnicianUser(id: $id) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: { id },
      })
      .pipe(map((resp: any) => resp.data.deleteTechnicianUser));
  }

  loadUsers(skip = 0, take = 40): Observable<any> {
    const query = gql`
      query LoadUsers($take: Int!, $skip: Int!) {
        loadUsers(take: $take, skip: $skip) {
          users {
            id
            name
            email
            active
            disable
            roll
          }
          totalRecords
        }
      }
    `;
    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: {
          skip,
          take,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.loadUsers;
        })
      );
  }

  searchUsers(search: string, skip = 0, take = 40): Observable<any> {
    const query = gql`
      query SearchUsers($search: String!, $take: Int!, $skip: Int!) {
        searchUsers(search: $search, take: $take, skip: $skip) {
          users {
            id
            name
            email
            active
            disable
            roll
          }
          totalRecords
        }
      }
    `;
    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: {
          search,
          skip,
          take,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.searchUsers;
        })
      );
  }

  loadUserById(id: string): Observable<any> {
    const query = gql`
      query LoadUserById($loadUserByIdId: String!) {
        loadUserById(id: $loadUserByIdId) {
          id
          name
          email
          roll
          active
          disable
          access {
            table {
              id
              name
            }
            read
            write
            delete
          }
        }
      }
    `;
    return this.apollo
      .query({
        fetchPolicy: 'no-cache',
        query,
        variables: {
          loadUserByIdId: id,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.loadUserById;
        })
      );
  }

  updateUser(
    id: string,
    form: FormGroup,
    permisos: FormGroup
  ): Observable<any> {
    const myArray = this.buildAccessPayload(permisos);
    const roll = form.get('roll')?.value;

    const data = {
      id: id,
      name: form.get('name')?.value || undefined,
      email: form.get('email')?.value || undefined,
      roll: roll.name,
      active: form.get('active')?.value,
      disable: form.get('disable')?.value,
      access: myArray,
    };

    const mutation = gql`
      mutation UpdateUser($data: inputUser!) {
        updateUser(data: $data) {
          ok
          message
        }
      }
    `;
    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          data,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.updateUser;
        })
      );
  }

  private buildAccessPayload(permisos: FormGroup): any[] {
    const accessObjet = permisos.controls['access'].value as Record<string, string[] | null>;
    const payload: any[] = [];

    for (const clave in accessObjet) {
      const selectedPermissionsValue = accessObjet[clave];
      const selectedPermissions: string[] = Array.isArray(selectedPermissionsValue)
        ? (selectedPermissionsValue as string[])
        : [];

      payload.push({
        table: { id: clave.toString() },
        read: selectedPermissions.includes('read'),
        write: selectedPermissions.includes('write'),
        delete: selectedPermissions.includes('delete'),
      });
    }

    return payload;
  }

  getControl(): Observable<any> {
    const query = gql`
      query GetControl {
        getControl {
          control {
            id
            name
          }
          totalRecords
        }
      }
    `;

    return this.apollo
      .query({
        query,
      })
      .pipe(
        map((resp: any) => {
          return resp.data.getControl.control;
        })
      );
  }

  getPermiso(tabla: string | undefined, tipo: string): boolean {
    this.access = JSON.parse(localStorage.getItem('access')!);
    this.roll = localStorage.getItem('roll');
    this.roll && this.roll === 'ADMINISTRADOR'
      ? (this.USA = true)
      : (this.USA = false);

    let resul = true;
    if (tabla == undefined && this.USA == false) {
      return false;
    }

    if (this.USA == true) return true;

    this.access.some((valor: any) => {
      if (
        tabla != undefined &&
        valor.table['name'] == tabla &&
        valor[tipo] == false
      ) {
        resul = false;
      }
    });

    return resul;
  }

  getRoll(): string | null {
    return localStorage.getItem('roll');
  }

  hasAnyRole(roles: string[]): boolean {
    const currentRoll = this.getRoll();
    if (!currentRoll) {
      return false;
    }

    return roles.includes(currentRoll);
  }

  deleteUser(id: string): Observable<any> {
    const mutation = gql`
      mutation DeleteUser($deleteUserId: String!) {
        deleteUser(id: $deleteUserId) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          deleteUserId: id,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.deleteUser;
        })
      );
  }

  disableUser(id: string): Observable<any> {
    const mutation = gql`
      mutation DisableUser($disableUserId: String!) {
        disableUser(id: $disableUserId) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        fetchPolicy: 'no-cache',
        mutation,
        variables: {
          disableUserId: id,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.disableUser;
        })
      );
  }

  sendMailActivated(email: string): Observable<any> {
    const mutation = gql`
      mutation DisableUser($email: String!) {
        sendMailActivate(email: $email) {
          ok
          message
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation,
        variables: {
          email,
        },
      })
      .pipe(
        map((resp: any) => {
          return resp.data.sendMailActivate;
        })
      );
  }
}
